import React, { useMemo } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Stack
} from '@mui/material';
import { AccessTime } from '@mui/icons-material';

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  label?: string;
  size?: 'small' | 'medium';
  use24Hour?: boolean;
}

// Generate time options in 30-minute increments
const generateTimeOptions = () => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(time);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Format time for display
const formatTimeDisplay = (time24: string, use24Hour: boolean): string => {
  if (!time24 || !time24.includes(':')) return time24;

  const [hours, minutes] = time24.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time24;

  if (use24Hour) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } else {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }
};

// Normalize time to nearest 30-minute interval
const normalizeToHalfHour = (time: string): string => {
  if (!time || !time.includes(':')) return '09:00';

  const [hours, minutes] = time.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return '09:00';

  // Round to nearest 30 minutes
  const roundedMinutes = minutes < 15 ? 0 : minutes < 45 ? 30 : 0;
  const roundedHours = minutes >= 45 ? (hours + 1) % 24 : hours;

  return `${roundedHours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
};

export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  label,
  size = 'small',
  use24Hour = true
}) => {
  // Normalize the current value to match available options
  const normalizedValue = useMemo(() => {
    const normalized = normalizeToHalfHour(value);
    // Ensure the normalized value exists in our options
    return TIME_OPTIONS.includes(normalized) ? normalized : '09:00';
  }, [value]);

  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value);
  };

  return (
    <Stack spacing={0.5}>
      <FormControl size={size} error={error} disabled={disabled}>
        <Select
          value={normalizedValue}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccessTime sx={{ fontSize: 18, color: 'action.active' }} />
              <span>{formatTimeDisplay(selected as string, use24Hour)}</span>
            </Stack>
          )}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
                '& .MuiMenuItem-root': {
                  fontSize: '14px',
                  py: 1
                }
              }
            }
          }}
          sx={{
            minWidth: use24Hour ? 130 : 150,
            bgcolor: 'background.paper',
            '& .MuiSelect-select': {
              py: 1,
              pl: 1.5,
              pr: 3,
              fontSize: '14px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main'
            }
          }}
        >
          {TIME_OPTIONS.map((time) => (
            <MenuItem key={time} value={time}>
              {formatTimeDisplay(time, use24Hour)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};
